import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '../api/products';
import { categoriesApi } from '../api/categories';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import {
  AvailableBadge,
  Card,
  PageHead,
} from '../components/ui';
import { StoreSelector } from '../components/StoreSelector';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import { money } from '../lib/format';
import type { Category, Product, ProductCreateRequest } from '../types';

export function ProductsPage() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const { selectedStoreId } = useStores();
  const storeId = selectedStoreId;

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [imaging, setImaging] = useState<Product | null>(null);

  // Debounce the search box.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function onSearchChange(v: string) {
    setSearch(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(v), 300);
  }

  const categoriesQ = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list({ storeId, activeOnly: false }),
  });

  const productsQ = useQuery({
    queryKey: ['products', storeId, categoryId, debounced],
    queryFn: () =>
      productsApi.list({ storeId, categoryId, q: debounced }),
  });

  const categoryName = useMemo(() => {
    const map = new Map((categoriesQ.data ?? []).map((c) => [c.id, c.name]));
    return (id: number) => map.get(id) ?? `#${id}`;
  }, [categoriesQ.data]);

  const invalidateProducts = () =>
    qc.invalidateQueries({ queryKey: ['products'] });

  const availability = useMutation({
    mutationFn: ({ id, available }: { id: number; available: boolean }) =>
      productsApi.setAvailability(id, available),
    onSuccess: invalidateProducts,
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => productsApi.deactivate(id),
    onSuccess: invalidateProducts,
  });

  const categories = categoriesQ.data ?? [];

  return (
    <>
      <PageHead
        title="Products"
        subtitle="Manage menu items, pricing, images and availability."
        actions={
          <button
            className="btn btn-primary"
            onClick={() => setCreating(true)}
            disabled={categories.length === 0}
          >
            + New Product
          </button>
        }
      />

      <div className="toolbar">
        {isSuperAdmin && <StoreSelector allowAll label="Store" />}
        <input
          style={{ width: 240 }}
          placeholder="Search products…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <select
          style={{ width: 'auto', minWidth: 160 }}
          value={categoryId ?? ''}
          onChange={(e) =>
            setCategoryId(e.target.value === '' ? null : Number(e.target.value))
          }
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {productsQ.isLoading ? (
        <Loading />
      ) : productsQ.isError ? (
        <ErrorState
          message={toErrorMessage(productsQ.error)}
          onRetry={productsQ.refetch}
        />
      ) : !productsQ.data || productsQ.data.length === 0 ? (
        <Card>
          <Empty
            icon="🍔"
            title="No products found"
            hint={
              categories.length === 0
                ? 'Create a category first, then add products.'
                : 'Try a different search or category filter.'
            }
          />
        </Card>
      ) : (
        <div className="product-grid">
          {productsQ.data.map((p) => (
            <div className="product-card" key={p.id}>
              <div className="product-thumb">
                {p.primaryImageUrl ? (
                  <img src={p.primaryImageUrl} alt={p.name} loading="lazy" />
                ) : (
                  <span className="placeholder">🍔</span>
                )}
              </div>
              <div className="product-body">
                <div className="p-name">{p.name}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {categoryName(p.categoryId)}
                  {p.sku ? ` · ${p.sku}` : ''}
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="p-price">{money(p.price)}</span>
                  <AvailableBadge available={p.available} />
                </div>
                {!p.active && (
                  <span className="badge badge-gray">Deactivated</span>
                )}
              </div>
              <div className="product-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditing(p)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setImaging(p)}
                >
                  Image
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() =>
                    availability.mutate({ id: p.id, available: !p.available })
                  }
                  disabled={availability.isPending}
                >
                  {p.available ? 'Mark unavailable' : 'Mark available'}
                </button>
                {p.active && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      if (window.confirm(`Deactivate “${p.name}”?`))
                        deactivate.mutate(p.id);
                    }}
                  >
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ProductFormModal
          title="New Product"
          categories={categories}
          initial={{
            categoryId: categories[0]?.id ?? 0,
            storeId,
            name: '',
            sku: '',
            description: '',
            price: 0,
            gstRate: 5,
            gstApplicable: true,
            active: true,
            available: true,
          }}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidateProducts();
          }}
          submit={(d) => productsApi.create(d)}
        />
      )}

      {editing && (
        <ProductFormModal
          title={`Edit ${editing.name}`}
          categories={categories}
          initial={{
            categoryId: editing.categoryId,
            storeId: editing.storeId,
            name: editing.name,
            sku: editing.sku ?? '',
            description: editing.description ?? '',
            price: editing.price,
            gstRate: editing.gstRate,
            gstApplicable: editing.gstApplicable,
            active: editing.active,
            available: editing.available,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidateProducts();
          }}
          submit={(d) => productsApi.update(editing.id, d)}
        />
      )}

      {imaging && (
        <ImageUploadModal
          product={imaging}
          onClose={() => setImaging(null)}
          onSaved={() => {
            invalidateProducts();
          }}
        />
      )}
    </>
  );
}

function ProductFormModal({
  title,
  categories,
  initial,
  onClose,
  onSaved,
  submit,
}: {
  title: string;
  categories: Category[];
  initial: ProductCreateRequest;
  onClose: () => void;
  onSaved: () => void;
  submit: (d: ProductCreateRequest) => Promise<Product>;
}) {
  const [draft, setDraft] = useState<ProductCreateRequest>(initial);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<ProductCreateRequest>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const mutation = useMutation({
    mutationFn: () => submit(draft),
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      wide
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={
              mutation.isPending ||
              !draft.name ||
              !draft.categoryId ||
              draft.price < 0
            }
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field-row">
          <div className="field">
            <label>Name *</label>
            <input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              autoFocus
              required
            />
          </div>
          <div className="field">
            <label>Category *</label>
            <select
              value={draft.categoryId}
              onChange={(e) => set({ categoryId: Number(e.target.value) })}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label>SKU</label>
            <input
              value={draft.sku ?? ''}
              onChange={(e) => set({ sku: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Price (₹) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.price}
              onChange={(e) => set({ price: Number(e.target.value) })}
              required
            />
          </div>
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            value={draft.description ?? ''}
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label>GST Rate (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.gstRate ?? 0}
              onChange={(e) => set({ gstRate: Number(e.target.value) })}
            />
          </div>
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <div className="checkbox-row" style={{ paddingBottom: 9 }}>
              <input
                id="gstApplicable"
                type="checkbox"
                checked={draft.gstApplicable ?? true}
                onChange={(e) => set({ gstApplicable: e.target.checked })}
              />
              <label htmlFor="gstApplicable">GST applicable</label>
            </div>
          </div>
        </div>
        <div className="row gap-sm" style={{ marginTop: 4 }}>
          <div className="checkbox-row">
            <input
              id="prodActive"
              type="checkbox"
              checked={draft.active ?? true}
              onChange={(e) => set({ active: e.target.checked })}
            />
            <label htmlFor="prodActive">Active</label>
          </div>
          <div className="checkbox-row" style={{ marginLeft: 20 }}>
            <input
              id="prodAvail"
              type="checkbox"
              checked={draft.available ?? true}
              onChange={(e) => set({ available: e.target.checked })}
            />
            <label htmlFor="prodAvail">Available</label>
          </div>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>
          Save the product first, then use “Image” to upload photos.
        </div>
      </form>
    </Modal>
  );
}

function ImageUploadModal({
  product,
  onClose,
  onSaved,
}: {
  product: Product;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [makePrimary, setMakePrimary] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>(product.imageUrls ?? []);

  const mutation = useMutation({
    mutationFn: () => productsApi.uploadImage(product.id, file!, makePrimary),
    onSuccess: (updated) => {
      setImages(updated.imageUrls ?? []);
      setFile(null);
      setPreview(null);
      onSaved();
    },
    onError: (err) => setError(toErrorMessage(err)),
  });

  function onPick(f: File | null) {
    setError(null);
    setFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  }

  return (
    <Modal
      open
      title={`Images · ${product.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={() => mutation.mutate()}
            disabled={!file || mutation.isPending}
          >
            {mutation.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}

      {images.length > 0 && (
        <div className="field">
          <label>Current images</label>
          <div className="row gap-sm flex-wrap">
            {images.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                style={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border:
                    url === product.primaryImageUrl
                      ? '2px solid var(--red)'
                      : '1px solid var(--line)',
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="field">
        <label>Upload new image</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
      </div>

      <div className="img-preview">
        {preview ? (
          <img src={preview} alt="preview" />
        ) : (
          <span className="text-muted">Preview will appear here</span>
        )}
      </div>

      <div className="checkbox-row" style={{ marginTop: 12 }}>
        <input
          id="makePrimary"
          type="checkbox"
          checked={makePrimary}
          onChange={(e) => setMakePrimary(e.target.checked)}
        />
        <label htmlFor="makePrimary">Set as primary image</label>
      </div>
    </Modal>
  );
}
